/**
 * RadialProgress — a lightweight SVG donut ring with a centred label slot.
 *
 * The subframe library only ships a linear Progress bar and a heavy recharts
 * PieChart, so this is the one genuinely net-new primitive the redesign needs
 * (NAU-247). Kept dependency-free and self-contained: a fixed-size wrapper
 * avoids the distort-on-flex pitfall called out in the design review.
 */

interface RadialProgressProps {
  /** 0–100 */
  value: number;
  /** Outer diameter in px */
  size?: number;
  /** Ring thickness in px */
  strokeWidth?: number;
  /** CSS colour for the filled arc (e.g. "var(--color-success-600)") */
  colorVar?: string;
  /** CSS colour for the unfilled track */
  trackVar?: string;
  /** Centre content (percentage, label, …) */
  children?: React.ReactNode;
  className?: string;
}

export function RadialProgress({
  value,
  size = 132,
  strokeWidth = 12,
  colorVar = "var(--color-primary-navy-800)",
  trackVar = "var(--color-neutral-200)",
  children,
  className,
}: RadialProgressProps) {
  const clamped = Math.max(0, Math.min(100, value));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <div
      className={`relative flex flex-none items-center justify-center ${className ?? ""}`}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90"
        aria-hidden
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={trackVar}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={colorVar}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 600ms ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children}
      </div>
    </div>
  );
}
